<script setup lang="ts">
import { SEO } from '@/utils/constants'
import { getBreadcrumbsJsonLd } from '~~/src/helpers/serp'
const { colors } = useTheme()

const jsonldBreadcrumbs = getBreadcrumbsJsonLd([
  { name: 'Privacy Notice', url: '/privacy' },
])

useHead({
  script: [
    {
      hid: 'page-json-ld',
      type: 'application/ld+json',
      innerHTML: JSON.stringify(jsonldBreadcrumbs),
    },
  ],
})

useSeoMeta({
  title: `Privacy Notice | ${SEO.title}`,
})
</script>

<template>
  <NuxtLayout>
    <div flex pb-5>
      <main w-full>
        <div flex="~ col" gap-2>
          <Heading>Privacy Notice for Snack Prompt</Heading>

          <section>
            <Text :color="colors.bluishDarker">
              At Snack Prompt, we value your privacy and are committed to protecting
              your personal information. This Privacy Notice outlines the types of
              personal information we collect, how we collect it, and how we use and
              protect your information.
            </Text>
          </section>

          <section>
            <Heading level="4">
              Personal Information We Collect
            </Heading>

            <div :class="$style.listItem">
              <Text>1.</Text>

              <Text :color="colors.bluishDarker">
                We collect your email address, name, and avatar from your Google
                account when you connect to our platform.
              </Text>
            </div>
          </section>

          <section>
            <Heading level="4">
              How We Collect Personal Information
            </Heading>

            <div :class="$style.listItem">
              <Text>2.</Text>

              <Text :color="colors.bluishDarker">
                We collect your personal information through your Google account
                when you connect to our platform.
              </Text>
            </div>
          </section>

          <section>
            <Heading level="4">
              Why We Collect Personal Information
            </Heading>

            <div :class="$style.listItem">
              <Text>3.</Text>

              <Text :color="colors.bluishDarker">
                We collect this information to create your profile page and allow
                other users to follow you on our platform.
              </Text>
            </div>
          </section>

          <section>
            <Heading level="4">
              Sharing Personal Information with Third Parties
            </Heading>

            <div :class="$style.listItem">
              <Text>4.</Text>

              <Text :color="colors.bluishDarker">
                We do not share your personal information with third parties, but we
                use Google Analytics and the Facebook Pixel for analytics and
                marketing purposes.
              </Text>
            </div>
          </section>

          <section>
            <Heading level="4">
              Storing and Protecting Personal Information
            </Heading>

            <div :class="$style.listItem">
              <Text>5.</Text>

              <Text :color="colors.bluishDarker">
                We store your personal information securely on AWS
                (Amazon Web Services).
              </Text>
            </div>
          </section>

          <section>
            <Heading level="4">
              Data Retention Policy
            </Heading>

            <div :class="$style.listItem">
              <Text>6.</Text>

              <Text :color="colors.bluishDarker">
                We do not have a specific data retention policy in place.
              </Text>
            </div>
          </section>

          <section>
            <Heading level="4">
              Accessing, Modifying, or Deleting Personal Information
            </Heading>

            <div :class="$style.listItem">
              <Text>7.</Text>

              <Text :color="colors.bluishDarker">
                You can access, modify, or delete your personal information by
                visiting the Profile Edit section on our website.
              </Text>
            </div>
          </section>

          <section>
            <Heading level="4">
              Age Restrictions
            </Heading>

            <div :class="$style.listItem">
              <Text>8.</Text>

              <Text :color="colors.bluishDarker">
                We do not have age restrictions for using our platform.
              </Text>
            </div>
          </section>

          <section>
            <Heading level="4">
              Cookies and Similar Technologies
            </Heading>

            <div :class="$style.listItem">
              <Text>9.</Text>

              <Text :color="colors.bluishDarker">
                We use Google Analytics and the Facebook Pixel for analytics and
                marketing purposes. By using our platform, you agree to the use of
                these technologies.
              </Text>
            </div>
          </section>

          <section>
            <Text :color="colors.bluishDarker">
              If you have any questions or concerns about our Privacy Notice,
              please feel free to contact us.
            </Text>
          </section>
        </div>
      </main>
    </div>
  </NuxtLayout>
</template>

<style lang="scss" module>
.listItem {
  --at-apply: ml-4 flex gap-2;
}
</style>
